import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Compass, Mountain, Users, ArrowRight, Check, TreePine, Heart, Leaf } from 'lucide-react';
import WhatsAppIcon from '../components/WhatsAppIcon';
import { useLanguage } from '../contexts/LanguageContext';
import { blogPosts as blogPostsData } from '../data/blogPosts';
import '../styles/Home.css';

const Home = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    { icon: <Compass size={40} />, title: t('home.featAvoidCrowdsTitle'), description: t('home.featAvoidCrowdsDesc') },
    { icon: <Mountain size={40} />, title: t('home.featAuthenticTitle'), description: t('home.featAuthenticDesc') },
    { icon: <Users size={40} />, title: t('home.featFlexibilityTitle'), description: t('home.featFlexibilityDesc') }
  ];

  const experienceCategories = [
    {
      id: 1,
      title: t('home.catDailyTitle'),
      subtitle: t('home.catDailySub'),
      description: t('home.catDailyDesc'),
      image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop',
      duration: t('home.durationDay'),
      price: 'EUR 40',
      link: '/services'
    },
    {
      id: 2,
      title: t('home.catDiscoveryTitle'),
      subtitle: t('home.catDiscoverySub'),
      description: t('home.catDiscoveryDesc'),
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop',
      duration: t('home.duration35'),
      price: 'EUR 299',
      link: '/experiences'
    },
    {
      id: 3,
      title: t('home.catActiveTitle'),
      subtitle: t('home.catActiveSub'),
      description: t('home.catActiveDesc'),
      image: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=600&fit=crop',
      duration: t('home.duration27'),
      price: 'EUR 189',
      link: '/experiences'
    }
  ];

  const sustainability = [
    {
      icon: <TreePine size={48} />,
      title: t('home.sustReforestTitle'),
      description: t('home.sustReforestDesc'),
      image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=800&h=600&fit=crop'
    },
    {
      icon: <Heart size={48} />,
      title: t('home.sustCommunityTitle'),
      description: t('home.sustCommunityDesc'),
      image: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=800&h=600&fit=crop'
    },
    {
      icon: <Leaf size={48} />,
      title: t('home.sustResponsibleTitle'),
      description: t('home.sustResponsibleDesc'),
      image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=800&h=600&fit=crop'
    }
  ];

  const destinations = [
    { name: 'Funchal', image: 'https://images.unsplash.com/photo-1571406761758-9a3eed5338ef?w=800&h=600&fit=crop' },
    { name: 'Porto Moniz', image: 'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800&h=600&fit=crop' },
    { name: 'Santana', image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop' },
    { name: 'Pico Ruivo', image: 'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=800&h=600&fit=crop' },
    { name: 'Cabo Girão', image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=600&fit=crop' },
    { name: 'Laurissilva', image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=800&h=600&fit=crop' }
  ];

  // Pull top 3 posts from JSON
  const blogPosts = blogPostsData.slice(0, 3);

  const faqs = [
    { question: 'What makes Madeira Outdoors tours unique?', answer: 'We design small-group and private tours focused on authentic experiences, avoiding crowds and showcasing Madeira\'s hidden treasures with expert local guides.' },
    { question: 'Which destinations can I visit?', answer: 'From Funchal to Porto Moniz, Pico Ruivo to Laurisilva Forest, we offer guided experiences across all of Madeira\'s top attractions with safe transport and expert planning.' },
    { question: 'Can I customize my itinerary?', answer: 'Absolutely! All our tours are flexible and can be personalized to match your interests, whether hiking, cultural immersion, or nature exploration.' },
    { question: 'What is the best time to visit Madeira?', answer: 'Madeira enjoys year-round mild weather. Spring (March-May) and autumn (September-November) offer ideal hiking conditions with fewer crowds.' },
    { question: 'Are your guides certified?', answer: 'Yes, all our guides are certified professionals and local experts with deep knowledge of Madeira\'s culture, nature, and history.' }
  ];

  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero-modern">
        <div className="hero-overlay-modern"></div>
        <div className="hero-content-modern container">
          <div className={`hero-text-modern ${isVisible ? 'animated fadeIn' : 'animated'}`}>
            <h1 className="display-large hero-title-modern">
              {t('home.heroTitle1')}<br />{t('home.heroTitle2')}
            </h1>
            <p className="body-large hero-subtitle-modern">{t('home.heroSubtitle')}</p>
            <div className="hero-buttons">
              <Link to="/experiences" className="btn-primary" data-testid="hero-see-tours">
                {t('home.ctaSeeTours')}
                <ArrowRight size={20} />
              </Link>
              <Link to="/custom-trip" className="btn-secondary-solid" data-testid="hero-customize">
                {t('home.ctaCustomize')}
              </Link>
            </div>
          </div>

          {/* Google Reviews badge (Elfsight) */}
          <div className="hero-reviews-badge">
            <div className="elfsight-app-6a53f69e-3832-484b-bb0d-286687b10b6f" data-elfsight-app-lazy></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="features-content-wrapper">
            <div className="features-text">
              <h2 className="heading-2">{t('home.featuresTitle')}</h2>
              <p className="body-large">{t('home.featuresLead')}</p>
              <p className="body-medium">{t('home.featuresSub')}</p>
              <div className="features-list">
                {features.map((feature, index) => (
                  <div key={index} className="feature-item-inline">
                    <div className="feature-icon-small">{feature.icon}</div>
                    <div className="feature-text-inline">
                      <h3 className="feature-title-small">{feature.title}</h3>
                      <p className="feature-description-small">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/custom-trip" className="btn-primary">
                {t('home.ctaCustomize')}
                <ArrowRight size={20} />
              </Link>
            </div>
            <div className="features-image">
              <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800&h=900&fit=crop" alt="Customize your Madeira trip" />
            </div>
          </div>
        </div>
      </section>

      {/* Experience Categories */}
      <section className="experiences-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">{t('home.experiencesTitle')}</h2>
            <p className="body-large">{t('home.experiencesSubtitle')}</p>
          </div>
          <div className="experience-categories-grid">
            {experienceCategories.map((category) => (
              <Link key={category.id} to={category.link} className="experience-category-card">
                <div className="experience-category-image-wrapper" style={{ backgroundImage: `url(${category.image})` }}>
                  <img src={category.image} alt={category.title} className="experience-category-image" style={{ opacity: 0 }} />
                  <div className="experience-category-overlay"></div>
                </div>
                <div className="experience-category-content">
                  <h3 className="experience-category-title">{category.title}</h3>
                  <p className="experience-category-subtitle">{category.subtitle}</p>
                  <p className="experience-category-description">{category.description}</p>
                  <div className="experience-category-meta">
                    <span className="experience-category-duration">{category.duration}</span>
                    <span className="experience-category-price">
                      <span className="from">{t('home.from')} </span>{category.price}
                    </span>
                  </div>
                  <span className="experience-category-link">{t('home.seeMore')}</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/experiences" className="btn-primary">
              {t('home.seeAll')}
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="team-section">
        <div className="container">
          <div className="team-content">
            <div className="team-text">
              <h2 className="heading-2">{t('home.teamTitle')}</h2>
              <p className="body-large">{t('home.teamLead')}</p>
              <p className="body-medium">{t('home.teamBody')}</p>
              <Link to="/about" className="btn-secondary">
                {t('home.teamCta')}
                <ArrowRight size={20} />
              </Link>
            </div>
            <div className="team-image">
              <img src="https://images.unsplash.com/photo-1539635278303-d4002c07eae3?w=800&h=600&fit=crop" alt="Madeira Outdoors Team" />
            </div>
          </div>
        </div>
      </section>

      {/* Sustainability */}
      <section className="sustainability-section">
        <div className="container">
          <div className="section-header-center">
            <h2 className="heading-2">{t('home.sustainabilityTitle')}</h2>
            <p className="body-large">{t('home.sustainabilityLead')}</p>
          </div>
          <div className="sustainability-grid">
            {sustainability.map((item, index) => (
              <div key={index} className="sustainability-card">
                <div className="sustainability-image-wrapper">
                  <img src={item.image} alt={item.title} className="sustainability-image" />
                </div>
                <div className="sustainability-content">
                  <div className="sustainability-icon">{item.icon}</div>
                  <h3 className="heading-3">{item.title}</h3>
                  <p className="body-medium">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/mission" className="btn-secondary">
              {t('home.sustCta')}
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="destinations-section">
        <div className="container">
          <div className="section-header-center">
            <h2 className="heading-2">{t('home.destinationsTitle')}</h2>
            <p className="body-large">{t('home.destinationsLead')}</p>
          </div>
          <div className="destinations-grid">
            {destinations.map((destination, index) => (
              <div key={index} className="destination-card">
                <img src={destination.image} alt={destination.name} className="destination-image" />
                <div className="destination-overlay">
                  <h3 className="destination-name">{destination.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog */}
      <section className="blog-section">
        <div className="container">
          <div className="section-header-center">
            <h2 className="heading-2">{t('home.blogTitle')}</h2>
            <p className="body-large">{t('home.blogLead')}</p>
          </div>
          <div className="blog-grid">
            {blogPosts.map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} className="blog-card" data-testid={`home-blog-${post.slug}`}>
                <img src={post.image} alt={post.title} className="blog-image" />
                <div className="blog-content">
                  <h3 className="blog-title">{post.title}</h3>
                  <p className="blog-description">{post.excerpt}</p>
                  <span className="blog-link">{t('home.blogReadMore')}</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="section-cta">
            <Link to="/blog" className="btn-secondary">
              {t('home.blogAllCta')}
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Contact & FAQ */}
      <section className="contact-faq-section">
        <div className="container">
          <div className="contact-faq-grid">
            <div className="contact-form-wrapper">
              <h2 className="heading-2">{t('home.contactTitle')}</h2>
              <p className="body-medium">{t('home.contactLead')}</p>
              <form className="contact-form">
                <input type="text" placeholder={t('home.formName')} className="form-input" required />
                <input type="email" placeholder={t('home.formEmail')} className="form-input" required />
                <input type="tel" placeholder={t('home.formPhone')} className="form-input" />
                <textarea placeholder={t('home.formMsg')} className="form-textarea" rows="4" required></textarea>
                <div className="form-benefits">
                  <div className="benefit-item"><Check size={18} /><span>{t('home.formBenefit1')}</span></div>
                  <div className="benefit-item"><Check size={18} /><span>{t('home.formBenefit2')}</span></div>
                  <div className="benefit-item"><Check size={18} /><span>{t('home.formBenefit3')}</span></div>
                </div>
                <button type="submit" className="btn-primary form-submit">
                  {t('home.formSubmit')}
                  <ArrowRight size={20} />
                </button>
              </form>
              <div className="contact-info">
                <p className="contact-info-title">{t('home.contactInfoTitle')}</p>
                <a href="mailto:bookings@madeiraoutdoors.com" className="contact-info-link">bookings@madeiraoutdoors.com</a>
                <a href="https://wa.me/351968656613" target="_blank" rel="noopener noreferrer" className="contact-info-link">
                  <WhatsAppIcon size={20} /> WhatsApp · +351 968 656 613
                </a>
              </div>
            </div>
            <div className="faq-wrapper">
              <h3 className="heading-3">{t('home.faqTitle')}</h3>
              <div className="faq-list">
                {faqs.map((faq, index) => (
                  <div key={index} className="faq-item">
                    <button className={`faq-question ${openFaq === index ? 'active' : ''}`} onClick={() => setOpenFaq(openFaq === index ? null : index)}>
                      <span>{faq.question}</span>
                      <ArrowRight size={20} className={`faq-icon ${openFaq === index ? 'rotated' : ''}`} />
                    </button>
                    {openFaq === index && (
                      <div className="faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
